import { FC, useState } from 'react'
import { Button, ConfirmBox } from '@corratech/pylot-ui'
import { useUI, ToastType } from '@corratech/pylot-ui/context'
import { Trash } from '@components/icons'
import { useTranslation } from 'next-i18next/pages'
import { Maybe, WishlistItemInterface, Wishlist } from '@pylot-data/pylotschema'
import { useRemoveProductsFromWishlist } from '../../Hooks/use-remove-products-from-wishlist'
import s from '@pagestyles/Wishlist.module.scss'

export type Props = {
    wishlist?: Wishlist
    item: Maybe<WishlistItemInterface>
    wishlistId: Maybe<string> | undefined
}

export const DeleteWishlistItem: FC<Props> = ({
    item,
    wishlist,
    wishlistId
}) => {
    const { t } = useTranslation(['wishlist'])
    const [isVisible, setIsVisible] = useState(false)
    const { openToast } = useUI()
    const { isLoading, removeProductsFromWishlist } =
        useRemoveProductsFromWishlist()
    const productName = item!.product?.name

    const showConfirm = () => setIsVisible(true)
    const closeConfirm = () => setIsVisible(false)

    const handleRemoveProduct = async () => {
        closeConfirm()
        try {
            const response = await removeProductsFromWishlist({
                wishlistId: String(wishlistId!),
                wishlistItemsIds: [String(item!.id)]
            })

            const userErrors =
                response?.data?.removeProductsFromWishlist?.user_errors?.filter(
                    Boolean
                )

            if (response?.errors?.length || userErrors?.length) {
                openToast(
                    userErrors?.[0]?.message ??
                        t('wishlist|Something went wrong. Please try again'),
                    ToastType.Danger
                )
                return
            }

            if (response?.data?.removeProductsFromWishlist?.wishlist) {
                openToast(
                    t(
                        'wishlist|"{{ name }}" has been removed from the wish list',
                        { name: productName }
                    ),
                    ToastType.Success
                )
            } else if (!response?.errors?.length) {
                openToast(
                    t('wishlist|Something went wrong. Please try again'),
                    ToastType.Danger
                )
            }
        } catch (e) {
            if (e) {
                openToast(
                    t('wishlist|Something went wrong. Please try again'),
                    ToastType.Danger
                )
            }
        }
    }

    return (
        <div className={s['delete-container']}>
            <Button
                type="button"
                variant="link"
                loading={isLoading}
                disabled={isLoading}
                onClick={showConfirm}
            >
                <Trash />
            </Button>

            <ConfirmBox
                show={isVisible}
                closeConfirm={closeConfirm}
                handleAction={handleRemoveProduct}
                confirmString={t('wishlist|remove')}
            >
                {t(
                    `wishlist|Are you sure you want to delete item "{{ name }}"? This action can't be undone.`,
                    { name: productName }
                )}
            </ConfirmBox>
        </div>
    )
}
